import React, { useState, useEffect, useRef } from 'react';
import { Star, Quote, ArrowLeft, ArrowRight } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const ROTATION_MS = 18000; // 18s — show each pair of reviews long enough to be read

const ReviewCard = ({ review }) => {
  if (!review) return null;
  return (
    <div
      data-testid="review-card"
      style={{
        background: 'rgba(255,255,255,0.04)',
        border: '1px solid rgba(172, 153, 105, 0.22)',
        borderRadius: '1rem',
        padding: '2rem 1.75rem',
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        boxShadow: '0 8px 24px rgba(0,0,0,0.18)'
      }}
    >
      <Quote size={28} style={{ color: 'var(--primary-gold)', marginBottom: '1rem', opacity: 0.6 }} />
      <p style={{
        fontFamily: "'Manrope', sans-serif",
        fontSize: '1rem',
        fontWeight: '400',
        color: 'rgba(255,255,255,0.95)',
        lineHeight: '1.7',
        marginBottom: '1.5rem',
        flex: 1
      }}>
        « {review.comment} »
      </p>
      <div className="stars-cascade is-visible" style={{ display: 'flex', gap: '0.15rem', marginBottom: '0.65rem' }}>
        {[...Array(5)].map((_, i) => (
          <Star key={i} size={14} fill="#ac9969" color="#ac9969" />
        ))}
      </div>
      <div>
        <p style={{ fontWeight: '700', color: 'var(--primary-gold)', fontSize: '0.92rem', marginBottom: '0.15rem' }}>
          {review.name}
        </p>
        <p style={{ color: 'rgba(255,255,255,0.45)', fontSize: '0.78rem', margin: 0 }}>
          {review.company}
        </p>
      </div>
    </div>
  );
};

const Testimonials = () => {
  const [reviews, setReviews] = useState([]);
  const [pairIndex, setPairIndex] = useState(0);
  const [visible, setVisible] = useState(true);
  const [starsAnimated, setStarsAnimated] = useState(false);
  const sectionRef = useRef(null);

  useEffect(() => {
    fetch(`${API_URL}/api/reviews`)
      .then(res => res.json())
      .then(data => { if (data && data.length > 0) setReviews(data); })
      .catch(() => {});
  }, []);

  // Trigger the 5-stars cascade animation when the section enters the viewport
  useEffect(() => {
    if (!sectionRef.current || starsAnimated) return;
    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach(e => {
          if (e.isIntersecting) {
            setStarsAnimated(true);
            obs.disconnect();
          }
        });
      },
      { threshold: 0.2 }
    );
    obs.observe(sectionRef.current);
    return () => obs.disconnect();
  }, [reviews.length, starsAnimated]);

  // Number of "pairs" we cycle through. Each step advances by 2 reviews.
  const pairCount = Math.max(1, Math.ceil(reviews.length / 2));

  // Smooth fade transition when switching pairs
  const changePair = (updater) => {
    setVisible(false);
    window.setTimeout(() => {
      setPairIndex(prev => updater(prev));
      setVisible(true);
    }, 400);
  };

  useEffect(() => {
    if (reviews.length === 0) return;
    const interval = setInterval(() => {
      changePair(prev => (prev + 1) % pairCount);
    }, ROTATION_MS);
    return () => clearInterval(interval);
  }, [reviews.length, pairCount]);

  if (reviews.length === 0) return null;

  const firstIdx = (pairIndex * 2) % reviews.length;
  const secondIdx = (firstIdx + 1) % reviews.length;
  const reviewA = reviews[firstIdx];
  const reviewB = reviews[secondIdx];

  const nextPair = () => changePair(prev => (prev + 1) % pairCount);
  const prevPair = () => changePair(prev => (prev - 1 + pairCount) % pairCount);
  const goToPair = (i) => changePair(() => i);

  return (
    <section className="section section-dark noise-overlay" id="avis" data-testid="testimonials-section" ref={sectionRef} style={{ padding: '5rem 0' }}>
      <div className="container" style={{ position: 'relative', zIndex: 1 }}>

        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: '3rem' }}>
          <span className="section-label-light">Ce qu'ils disent</span>
          <h2 style={{
            fontFamily: "'Playfair Display', serif",
            fontSize: 'clamp(1.75rem, 4vw, 2.5rem)',
            fontWeight: '500',
            color: 'white',
            marginBottom: '1rem'
          }}>
            Avis clients
          </h2>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.75rem' }}>
            <div
              data-testid="header-stars"
              className={`stars-cascade ${starsAnimated ? 'is-visible' : ''}`}
              style={{ display: 'flex', gap: '0.15rem' }}
            >
              {[...Array(5)].map((_, i) => (
                <Star key={i} size={18} fill="#ac9969" color="#ac9969" />
              ))}
            </div>
            <span style={{ fontWeight: '600', color: 'white', fontSize: '0.95rem' }}>
              5/5 · {reviews.length} avis Google
            </span>
          </div>
        </div>

        {/* Pair of reviews side by side - with fade transition */}
        <div className="reviews-pair-grid" style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
          gap: '1.5rem',
          maxWidth: '1080px',
          margin: '0 auto 2.5rem',
          alignItems: 'stretch',
          opacity: visible ? 1 : 0,
          transform: visible ? 'translateY(0)' : 'translateY(8px)',
          transition: 'opacity 400ms ease, transform 400ms ease'
        }}>
          <ReviewCard review={reviewA} />
          <ReviewCard review={reviewB} />
        </div>

        {/* Navigation */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '2rem', marginBottom: '2rem' }}>
          <button
            onClick={prevPair}
            data-testid="review-prev-btn"
            style={{
              background: 'rgba(255,255,255,0.05)',
              border: '1px solid rgba(255,255,255,0.15)',
              color: 'white',
              width: '44px',
              height: '44px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              transition: 'all 0.3s ease',
              borderRadius: '50%'
            }}
            aria-label="Avis précédents"
          >
            <ArrowLeft size={18} />
          </button>

          <span style={{ color: 'rgba(255,255,255,0.45)', fontSize: '0.8rem', fontWeight: '600', letterSpacing: '0.1em' }}>
            {pairIndex + 1} / {pairCount}
          </span>

          <button
            onClick={nextPair}
            data-testid="review-next-btn"
            style={{
              background: 'rgba(255,255,255,0.05)',
              border: '1px solid rgba(255,255,255,0.15)',
              color: 'white',
              width: '44px',
              height: '44px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              transition: 'all 0.3s ease',
              borderRadius: '50%'
            }}
            aria-label="Avis suivants"
          >
            <ArrowRight size={18} />
          </button>
        </div>

        {/* Dots */}
        <div style={{ display: 'flex', justifyContent: 'center', gap: '0.35rem', marginBottom: '2.5rem', flexWrap: 'wrap' }}>
          {Array.from({ length: pairCount }).map((_, i) => (
            <button
              key={i}
              onClick={() => goToPair(i)}
              aria-label={`Aller à la paire d'avis ${i + 1}`}
              style={{
                width: pairIndex === i ? '24px' : '6px',
                height: '6px',
                borderRadius: '3px',
                background: pairIndex === i ? 'var(--primary-gold)' : 'rgba(255,255,255,0.18)',
                border: 'none',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                padding: 0
              }}
            />
          ))}
        </div>

        {/* CTA - prominent gold button */}
        <div style={{ textAlign: 'center' }}>
          <a
            href="https://g.page/r/CVcEwDxwGAt0EBM/review"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="leave-review-btn"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '0.6rem',
              padding: '0.9rem 2rem',
              background: 'linear-gradient(135deg, var(--primary-gold) 0%, #c9b576 100%)',
              color: 'white',
              fontWeight: '700',
              fontSize: '0.95rem',
              letterSpacing: '0.02em',
              borderRadius: '2rem',
              textDecoration: 'none',
              boxShadow: '0 8px 24px rgba(172, 153, 105, 0.4)',
              transition: 'transform 0.2s ease, box-shadow 0.2s ease'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = '0 12px 32px rgba(172, 153, 105, 0.55)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 8px 24px rgba(172, 153, 105, 0.4)';
            }}
          >
            <Star size={16} fill="white" />
            Laisser un avis 5 étoiles
          </a>
        </div>
      </div>
    </section>
  );
};

export default Testimonials;
